import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Paper, Typography, TextField, Switch, FormControlLabel, Snackbar, Alert, Stack } from '@mui/material';
import { templateApi } from '../../services/api';

interface Template {
  id?: number;
  name: string;
  template_file: string;
  has_hero_image: boolean;
  has_sidebar: boolean;
  has_call_to_action: boolean;
  has_qnas: boolean;
  has_cards: boolean;
  has_content: boolean;
}

const TemplateEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [template, setTemplate] = useState<Template>({
    name: '',
    template_file: '',
    has_hero_image: false,
    has_sidebar: false,
    has_call_to_action: false,
    has_qnas: false,
    has_cards: false,
    has_content: false,
  });
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{ open: boolean; message: string; severity: 'success' | 'error'; }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (isEdit) {
      templateApi.getById(Number(id)).then(res => setTemplate(res.data)).catch(() => setNotification({ open: true, message: 'Failed to load template', severity: 'error' }));
    }
  }, [id, isEdit]);

  const handleChange = (field: keyof Template) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setTemplate(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await templateApi.update(Number(id), template);
        setNotification({ open: true, message: 'Template updated', severity: 'success' });
      } else {
        await templateApi.create(template);
        setNotification({ open: true, message: 'Template created', severity: 'success' });
      }
      setTimeout(() => navigate('/cms/templates'), 1000);
    } catch (error) {
      setNotification({ open: true, message: 'Failed to save template', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" mb={2}>{isEdit ? 'Edit Template' : 'New Template'}</Typography>
        <Stack spacing={2}>
          <TextField label="Name" value={template.name} onChange={handleChange('name')} fullWidth />
          <TextField label="Template File" value={template.template_file} onChange={handleChange('template_file')} fullWidth />
          <FormControlLabel control={<Switch checked={template.has_hero_image} onChange={handleChange('has_hero_image')} />} label="Has Hero Image" />
          <FormControlLabel control={<Switch checked={template.has_sidebar} onChange={handleChange('has_sidebar')} />} label="Has Sidebar" />
          <FormControlLabel control={<Switch checked={template.has_call_to_action} onChange={handleChange('has_call_to_action')} />} label="Has Call to Action" />
          <FormControlLabel control={<Switch checked={template.has_qnas} onChange={handleChange('has_qnas')} />} label="Has Q&A" />
          <FormControlLabel control={<Switch checked={template.has_cards} onChange={handleChange('has_cards')} />} label="Has Cards" />
          <FormControlLabel control={<Switch checked={template.has_content} onChange={handleChange('has_content')} />} label="Has Content" />
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </Stack>
      </Paper>
      <Snackbar open={notification.open} autoHideDuration={4000} onClose={() => setNotification({ ...notification, open: false })}>
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplateEditor; 